import React, { FC, useEffect, useState } from 'react';
import { GiHamburgerMenu } from 'react-icons/gi';
import { IoMdClose } from 'react-icons/io';
import { IoChevronDown } from 'react-icons/io5';
import { handleRipple } from '../../../helpers/ripple';
import {
  IconButton,
  LockScroll,
  NavbarSection,
  NavbarList,
  NavbarGroup,
  NavbarLink,
  NavbarGroupTrigger,
  NavbarSubList,
  NavbarSubLink,
  LoginButton,
  BitcoinLogo,
} from './styled';

interface NavbarItem {
  key: string;
  text: string;
  link?: string;
  openNewTab: boolean;
  children?: NavbarItem[];
}

interface ButtonData {
  buttonText: string | null | undefined;
  buttonLink: string | null | undefined;
}

interface Props {
  isOpen: boolean;
  onClick: () => void;
  navbarList: NavbarItem[];
  navbarMobileList: NavbarItem[];
  buttonData: ButtonData;
}

const Menu: FC<Props> = ({
  isOpen,
  onClick,
  navbarList,
  navbarMobileList,
  buttonData,
}) => {
  const [openDropdownKey, setOpenDropdownKey] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setOpenDropdownKey(null);
    }
  }, [isOpen]);

  const handleButtonClick = () => {
    if (typeof onClick === 'function') {
      onClick();
    }
  };

  const handleDropdownToggle = (key: string) => {
    setOpenDropdownKey(prevKey => (prevKey === key ? null : key));
  };

  return (
    <>
      <IconButton onClick={handleButtonClick} aria-label="Hamburger Menu">
        {isOpen ? (
          <IoMdClose size={40} color="#1c1c1c" />
        ) : (
          <GiHamburgerMenu size={40} color="#1c1c1c" />
        )}
      </IconButton>
      <LockScroll locked={isOpen} />
      <NavbarSection open={isOpen}>
        <NavbarList>
          {navbarList.map(item => {
            const hasChildren = Boolean(item.children && item.children.length);

            if (!hasChildren) {
              return (
                <NavbarLink
                  key={item.key}
                  href={item.link ?? undefined}
                  target={item.openNewTab ? '_blank' : undefined}
                  rel={item.openNewTab ? 'noopener noreferrer' : undefined}
                  onClick={handleButtonClick}
                >
                  {item.text}
                </NavbarLink>
              );
            }

            return (
              <NavbarGroup key={item.key}>
                <NavbarGroupTrigger
                  type="button"
                  onClick={() => handleDropdownToggle(item.key)}
                  isOpen={openDropdownKey === item.key}
                  aria-expanded={openDropdownKey === item.key}
                >
                  {item.text}
                  <IoChevronDown size={16} />
                </NavbarGroupTrigger>
                <NavbarSubList isOpen={openDropdownKey === item.key}>
                  {item.children!.map(child => (
                    <NavbarSubLink
                      key={child.key}
                      href={child.link ?? undefined}
                      target={child.openNewTab ? '_blank' : undefined}
                      rel={child.openNewTab ? 'noopener noreferrer' : undefined}
                    >
                      {child.text}
                    </NavbarSubLink>
                  ))}
                </NavbarSubList>
              </NavbarGroup>
            );
          })}
          {navbarMobileList.map(item => (
            <NavbarLink
              key={item.key}
              href={item.link ?? undefined}
              target={item.openNewTab ? '_blank' : undefined}
              rel={item.openNewTab ? 'noopener noreferrer' : undefined}
              onClick={handleButtonClick}
            >
              {item.text}
            </NavbarLink>
          ))}
        </NavbarList>
        <LoginButton
          href={buttonData.buttonLink ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleButtonClick}
          onMouseEnter={handleRipple}
        >
          <BitcoinLogo
            src="/images/floating-button-icon.png"
            alt="bitcoin logo"
          />
          {buttonData.buttonText}
        </LoginButton>
      </NavbarSection>
    </>
  );
};

export default Menu;
